import { useState, useMemo, ReactNode } from 'react';
import { CssBaseline } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ColorModeContext from './ColorModeContext';
// import useMediaQuery from '@mui/material/useMediaQuery'

import getTypographyMixin from './mixins/typography-mixin';
import getPaletteMixin from './mixins/palette-mixin';

import {
  createTheme,
  ThemeOptions,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';

const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(
    'light'
    // useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light'
  );

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    []
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  );
};

const getDesignTokens = (mode: 'light' | 'dark'): ThemeOptions => ({
  typography: getTypographyMixin,
  palette: getPaletteMixin(mode),
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.common.black, 0.05),
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-disabled': {
            backgroundColor: alpha(theme.palette.common.black, 0.02),
            '.MuiSelect-outlined': {
              WebkitTextFillColor: theme.palette.text.primary,
            },
            // '& .MuiOutlinedInput-notchedOutline': {
            //   borderColor: alpha(theme.palette.common.black, 0.04),
            // },
          },
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        a: {
          color: theme.palette.info.main,
          textDecoration: 'none',
        },
        'a:visited': {
          color: theme.palette.info.dark,
        },

        /* Notistack */
        '.notistack-SnackbarContainer': {
          position: 'absolute',
          top: theme.spacing(1),
          right: theme.spacing(1),
          overflow: 'hidden',
        },

        /* Gradient */
        '.gradient-text': {
          color: 'transparent',
          backgroundClip: 'text',
          backgroundImage:
            'linear-gradient(to left top, #4c1d95, #1e3a8a, #0c4a6e)',
        },
      }),
    },
  },
});

declare module '@mui/material/styles' {
  interface Theme {
    fadeShadows: ['none', string, string, string, string];
  }
  interface ThemeOptions {
    fadeShadows?: ['none', string, string, string, string];
  }
}

export default ThemeProvider;
